import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./CgtTopic.css";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon, ArrowRightIcon, ChevronLeft, ChevronRight, EditIcon, Plus, Search, TrashIcon } from "lucide-react";
import {toast} from 'react-toastify'

const API_BASE = "https://payyug.in/api";

const getActorId = () => {
  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;
  const rawUser = localStorage.getItem("user");

  if (!rawUser) return superAdminId;

  try {
    const parsed = JSON.parse(rawUser);
    return Number(parsed?.id || parsed?.userId || superAdminId) || superAdminId;
  } catch {
    return superAdminId;
  }
};

const CgtTopic = () => {
  const navigate = useNavigate();
  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;
  const actorId = getActorId();

  const [topicName, setTopicName] = useState("");
  const [description, setDescription] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const [paginationInfo, setPaginationInfo] = useState({
    total: 0,
    totalPages: 0,
  });

  const [editItem, setEditItem] = useState(null);
  const [editTopicName, setEditTopicName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const [deleteItemId, setDeleteItemId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1); 
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchTopics = async (page = currentPage) => {
    if (!superAdminId) return;

    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/cgt-topic/list`, {
        params: {
          superAdminId,
          page: pageSize === "all" ? 1 : page,
          limit: pageSize === "all" ? 999999 : Number(pageSize),
          search: debouncedSearch.trim(),
        },
      });

      if (res?.data?.success) {
        setRows(res.data.data || []);
        if (res.data.pagination) {
          setPaginationInfo({
            total: res.data.pagination.total,
            totalPages: pageSize === "all" ? 1 : res.data.pagination.totalPages,
          });
          if (pageSize !== "all") {
            setCurrentPage(res.data.pagination.page);
          }
        }
      } else {
        setRows(res?.data?.data || res?.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch CGT topics", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopics(currentPage);
  }, [superAdminId, currentPage, pageSize, debouncedSearch]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!topicName.trim() || !description.trim() || submitting) return;

    try {
      setSubmitting(true);
      const res=await axios.post(`${API_BASE}/cgt-topic/create`, {
        topicName: topicName.trim(),
        description: description.trim(),
        superAdminId,
        // createdBy: actorId,
      });
      setTopicName("");
      setDescription("");
      setCurrentPage(1);
      await fetchTopics(1);
      toast.success(res.data?.message||"CGT topic added successfully.");
    } catch (err) {
      console.error("Failed to add CGT topic", err);
      toast.error(err?.response?.data?.message || "Failed to add CGT topic.");
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteItemId(id);
  };

  const closeDeleteModal = () => {
    setDeleteItemId(null);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;
    try {
      const res=await axios.delete(`${API_BASE}/cgt-topic/delete/${deleteItemId}`);
      const updatedPage = rows.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      setCurrentPage(updatedPage);
      closeDeleteModal();
      await fetchTopics(updatedPage);
      toast.success(res.data?.message||"Topic deleted successfully.");
    } catch (err) {
      console.error("Delete topic failed", err);
      toast.error(err?.response?.data?.message || "Failed to delete topic.");
      closeDeleteModal();
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditTopicName(item?.topicName || item?.TopicName || "");
    setEditDescription(item?.description || item?.Description || "");
  };

  const closeEdit = () => {
    setEditItem(null);
    setEditTopicName("");
    setEditDescription("");
  };

  const onUpdate = async (e) => {
    e.preventDefault();
    if (!editItem?.id || !editTopicName.trim() || !editDescription.trim()) return;

    try {
      const res=await axios.put(`${API_BASE}/cgt-topic/update/${editItem.id}`, {
        topicName: editTopicName.trim(),
        description: editDescription.trim(),
        // updateBy: actorId,
      });
      await fetchTopics(currentPage);
      closeEdit();
      toast.success(res.data?.message||"Topic updated successfully.");
    } catch (err) {
      console.error("Update topic failed", err);
      toast.error(err?.response?.data?.message || "Failed to update topic.");
    }
  };

  const dataRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < paginationInfo.totalPages) setCurrentPage((prev) => prev + 1);
  };

  return (
    <div className="cgt-topic-container">
      <div className="cgt-topic-top-header">
        <ArrowLeftIcon size={16} onClick={() => navigate(-1)} className="cgt-topic-top-header-icon" />
        <h2>CGT Topic Dashboard</h2>
        <ArrowRightIcon size={16} onClick={() => navigate(1)} className="cgt-topic-top-header-icon" />
      </div>

      <div className="cgt-topic-section">
        <h3>Add New CGT Topic</h3>
        <form className="cgt-topic-form" onSubmit={onCreate}>
          <div className="cgt-topic-form-grid">
            <div className="cgt-topic-form-fields">
              <label>Topic Name</label>
              <input
                type="text"
                value={topicName}
                onChange={(e) => setTopicName(e.target.value)}
                placeholder="Enter topic name"
                required
              />
            </div>

            <div className="cgt-topic-form-fields cgt-topic-wide-fields">
              <label>Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter topic description"
                required
              />
            </div>

            <div className="cgt-topic-form-fields cgt-topic-btn-align">
              <button
                type="submit"
                className="cgt-topic-primary-btn cgt-topic-add-btn-flex"
                disabled={!topicName.trim() || !description.trim() || submitting}
              >
                <Plus size={16} />
                {submitting ? "Saving..." : "Add Topic"}
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="cgt-topic-section">
        <h3>CGT Topics Report List</h3>

        {/* Backend Filter Layout Controls */}
        <div className="cgt-topic-report-filter">
          <div className="cgt-topic-report-search-container">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="cgt-topic-report-search-input"
            />
          </div>

          <div className="cgt-topic-report-limit-container">
            <label>Show Entries:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(e.target.value);
                setCurrentPage(1);
              }}
              className="cgt-topic-report-limit-select"
            >
              <option value="10">10 rows</option>
              <option value="25">25 rows</option>
              <option value="50">50 rows</option>
              <option value="100">100 rows</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        {/* Dynamic Report Table Layout */}
        <div className="cgt-topic-report-table-wrapper">
          <table className="cgt-topic-report-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Topic Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="cgt-topic-report-table-status">Loading data...</td>
                </tr>
              ) : dataRows.length === 0 ? (
                <tr>
                  <td colSpan="4" className="cgt-topic-report-table-status">No matching records found.</td>
                </tr>
              ) : (
                dataRows.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>
                      {pageSize === "all" ? index + 1 : (currentPage - 1) * Number(pageSize) + index + 1}
                    </td>
                    <td>{item.topicName || item.TopicName || "-"}</td>
                    <td>{item.description || item.Description || "-"}</td>
                    <td className="cgt-topic-report-table-actions">
                      <button type="button" className="cgt-topic-report-table-edit" onClick={() => openEdit(item)}>
                        <EditIcon/>
                      </button>
                      <button type="button" className="cgt-topic-report-table-delete" onClick={() => openDeleteModal(item.id)}>
                        <TrashIcon/>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Backend Pagination Footer Bar */}
        {pageSize !== "all" && paginationInfo.totalPages > 1 && (
          <div className="cgt-topic-pagination-container">
            <span className="cgt-topic-pagination-stats">
              Showing Page <strong>{currentPage}</strong> of <strong>{paginationInfo.totalPages}</strong> ({paginationInfo.total} items total)
            </span>

            <div className="cgt-topic-pagination-buttons">
              <button
                type="button"
                onClick={handlePrevPage}
                disabled={currentPage === 1 || loading}
                className="cgt-topic-pagination-btn"
              >
                Prev
              </button>

              <button
                type="button"
                onClick={handleNextPage}
                disabled={currentPage === paginationInfo.totalPages || loading}
                className="cgt-topic-pagination-btn"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Form Modal View Layout */}
      {editItem && (
        <div className="cgt-topic-modal-overlay" onClick={closeEdit}>
          <div className="cgt-topic-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Update CGT Topic</h4>
            <form onSubmit={onUpdate} className="cgt-topic-form">
              <div className="cgt-topic-form-grid">
                <div className="cgt-topic-form-fields">
                  <label>Topic Name</label>
                  <input
                    type="text"
                    value={editTopicName}
                    onChange={(e) => setEditTopicName(e.target.value)}
                    required
                  />
                </div>
                <div className="cgt-topic-form-fields cgt-topic-wide-fields">
                  <label>Description</label>
                  <input
                    type="text"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="cgt-topic-modal-actions">
                <button type="button" className="cgt-topic-secondary-btn" onClick={closeEdit}>
                  Cancel
                </button>
                <button type="submit" className="cgt-topic-primary-btn">
                  Update Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Delete Confirmation Modal View Layout */}
      {deleteItemId && (
        <div className="cgt-topic-modal-overlay" onClick={closeDeleteModal}>
          <div className="cgt-topic-modal cgt-topic-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h4>Confirm Deletion</h4>
            <p style={{ fontSize: "14px", color: "#475569", margin: "0 0 1.5rem" }}>
              Are you absolutely sure you want to delete this topic record? This choice is permanent.
            </p>
            <div className="cgt-topic-modal-actions">
              <button type="button" className="cgt-topic-secondary-btn" onClick={closeDeleteModal}>
                Cancel
              </button>
              <button
                type="button"
                style={{ background: "#dc2626", color: "#fff" }}
                className="cgt-topic-primary-btn"
                onClick={confirmDelete}
              >
                Delete Record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CgtTopic;